import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, Plane, Hotel, Car, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function TravelSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [travel, setTravel] = useState<any[]>([]);
  const [crewMembers, setCrewMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: 'flight',
    title: '',
    crew_member_id: '',
    details: '',
    confirmation_number: '',
    date: '',
    time: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    loadTravel();
    loadCrewMembers();
  }, [eventId]);

  const loadCrewMembers = async () => {
    const { data } = await supabase
      .from('crew_members')
      .select('id, name, full_name, role')
      .eq('event_id', eventId)
      .order('name');
    if (data) setCrewMembers(data);
  };

  const loadTravel = async () => {
    const { data } = await supabase
      .from('travel_info')
      .select(`
        *,
        crew_members(id, name, full_name, role)
      `)
      .eq('event_id', eventId)
      .order('date, time');
    if (data) setTravel(data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      crew_member_id: formData.crew_member_id || null
    };

    if (editingId) {
      await supabase
        .from('travel_info')
        .update(payload)
        .eq('id', editingId);
    } else {
      await supabase
        .from('travel_info')
        .insert({ ...payload, event_id: eventId });
    }
    resetForm();
    loadTravel();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('travel_info').delete().eq('id', id);
    setDeleteId(null);
    loadTravel();
  };

  const resetForm = () => {
    setFormData({
      type: 'flight',
      title: '',
      crew_member_id: '',
      details: '',
      confirmation_number: '',
      date: '',
      time: '',
      location: '',
      notes: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const editEntry = (entry: any) => {
    setFormData({
      type: entry.type || 'flight',
      title: entry.title || '',
      crew_member_id: entry.crew_member_id || '',
      details: entry.details || '',
      confirmation_number: entry.confirmation_number || '',
      date: entry.date || '',
      time: entry.time || '',
      location: entry.location || '',
      notes: entry.notes || ''
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const types = [
    { value: 'flight', label: 'Flight', icon: Plane, color: 'bg-primary-500/10 text-primary-400' },
    { value: 'hotel', label: 'Hotel', icon: Hotel, color: 'bg-emerald-500/10 text-emerald-400' },
    { value: 'ground_transport', label: 'Ground Transport', icon: Car, color: 'bg-amber-500/10 text-amber-400' }
  ];

  const getTypeInfo = (type: string) => {
    return types.find(t => t.value === type) || types[0];
  };

  const getCrewMemberName = (crewMember: any) => {
    if (!crewMember) return null;
    return crewMember.name || crewMember.full_name;
  };

  const groupedTravel = crewMembers.reduce((acc, crew) => {
    acc[crew.id] = travel.filter(t => t.crew_member_id === crew.id);
    return acc;
  }, {} as Record<string, any[]>);

  const unassignedTravel = travel.filter(t => !t.crew_member_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Travel Information</h2>
          <p className="text-zinc-500 mt-1">Flights, hotels, and ground transportation by crew member</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Travel Info
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} Travel Information
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type" className="text-zinc-300">Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {types.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="crew_member_id" className="text-zinc-300">Crew Member</Label>
                <Select value={formData.crew_member_id} onValueChange={(value) => setFormData({ ...formData, crew_member_id: value })}>
                  <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue placeholder="Select crew member" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="">None (Unassigned)</SelectItem>
                    {crewMembers.map(crew => (
                      <SelectItem key={crew.id} value={crew.id}>
                        {crew.name || crew.full_name} {crew.role && `- ${crew.role}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="title" className="text-zinc-300">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Flight to NYC"
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="details" className="text-zinc-300">Details</Label>
              <Input
                id="details"
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                placeholder="e.g., United 1234 - JFK to LAX"
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="confirmation_number" className="text-zinc-300">Confirmation Number</Label>
                <Input
                  id="confirmation_number"
                  value={formData.confirmation_number}
                  onChange={(e) => setFormData({ ...formData, confirmation_number: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="location" className="text-zinc-300">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Terminal 4, Gate B12"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date" className="text-zinc-300">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="time" className="text-zinc-300">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-zinc-300">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Entry
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : travel.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No travel information added yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {crewMembers.map(crew => {
            const crewTravel = groupedTravel[crew.id] || [];
            if (crewTravel.length === 0) return null;

            return (
              <div key={crew.id} className="space-y-3">
                <div className="flex items-center gap-2 text-white font-medium">
                  <User className="w-4 h-4 text-zinc-400" />
                  <span>{crew.name || crew.full_name}</span>
                  {crew.role && <span className="text-zinc-500 text-sm">- {crew.role}</span>}
                </div>
                <div className="space-y-2 pl-6">
                  {crewTravel.map((entry) => {
                    const typeInfo = getTypeInfo(entry.type);
                    const Icon = typeInfo.icon;
                    return (
                      <div key={entry.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 hover:border-zinc-700/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`p-1.5 rounded-lg ${typeInfo.color}`}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div>
                                <h4 className="font-medium text-white">{entry.title}</h4>
                                {entry.details && (
                                  <p className="text-sm text-zinc-400">{entry.details}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                              {entry.date && (
                                <div className="text-zinc-400">
                                  <span className="text-zinc-500">Date:</span> {entry.date}
                                  {entry.time && <span> at {entry.time}</span>}
                                </div>
                              )}
                              {entry.confirmation_number && (
                                <div className="text-zinc-400">
                                  <span className="text-zinc-500">Confirmation:</span> {entry.confirmation_number}
                                </div>
                              )}
                              {entry.location && (
                                <div className="text-zinc-400 col-span-2">
                                  <span className="text-zinc-500">Location:</span> {entry.location}
                                </div>
                              )}
                            </div>

                            {entry.notes && (
                              <p className="text-xs text-zinc-500 mt-2 pt-2 border-t border-zinc-800">
                                {entry.notes}
                              </p>
                            )}
                          </div>

                          {permissions.canEdit && (
                            <div className="flex items-center gap-1 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => editEntry(entry)}
                                className="text-zinc-400 hover:text-white h-7 text-xs"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(entry.id)}
                                className="text-zinc-400 hover:text-red-400 h-7 w-7"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {unassignedTravel.length > 0 && (
            <div className="space-y-3">
              <div className="text-zinc-400 font-medium">Unassigned Travel</div>
              <div className="space-y-2">
                {unassignedTravel.map((entry) => {
                  const typeInfo = getTypeInfo(entry.type);
                  const Icon = typeInfo.icon;
                  return (
                    <div key={entry.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 hover:border-zinc-700/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-1.5 rounded-lg ${typeInfo.color}`}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-white">{entry.title}</h4>
                              {entry.details && (
                                <p className="text-sm text-zinc-400">{entry.details}</p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                            {entry.date && (
                              <div className="text-zinc-400">
                                <span className="text-zinc-500">Date:</span> {entry.date}
                                {entry.time && <span> at {entry.time}</span>}
                              </div>
                            )}
                            {entry.confirmation_number && (
                              <div className="text-zinc-400">
                                <span className="text-zinc-500">Confirmation:</span> {entry.confirmation_number}
                              </div>
                            )}
                            {entry.location && (
                              <div className="text-zinc-400 col-span-2">
                                <span className="text-zinc-500">Location:</span> {entry.location}
                              </div>
                            )}
                          </div>

                          {entry.notes && (
                            <p className="text-xs text-zinc-500 mt-2 pt-2 border-t border-zinc-800">
                              {entry.notes}
                            </p>
                          )}
                        </div>

                        {permissions.canEdit && (
                          <div className="flex items-center gap-1 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editEntry(entry)}
                              className="text-zinc-400 hover:text-white h-7 text-xs"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(entry.id)}
                              className="text-zinc-400 hover:text-red-400 h-7 w-7"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Travel Entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this travel information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
